'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { createWorkspaceFolder } from '@nx-frontend/shared/api/webnotes';
import { useOpenPaywallPopup } from '@nx-frontend/shared/hooks/use-open-paywall-popup';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  ITEMS_TO_SHOW_DUPLICATION_PROGRESS,
  startDuplicateFolderTaskEvent,
} from '@nx-frontend/shared/store/duplicate-folder';
import { addNoteEvent } from '@nx-frontend/shared/store/notes';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import {
  DialogPopupPayload,
  OrgInfoSubscriptionType,
  PaywallPopupPayload,
  PaywallPopupPricingReason,
  PaywallPopupReason,
  PopupType,
} from '@nx-frontend/shared/types';

export const useCreateFolder = () => {
  const t = useTranslations('webnotes');
  const router = useRouter();
  const [openModal, addNote] = useEvent([openModalEvent, addNoteEvent]);

  /**
   * Shows create folder modal and redirects to the created folder
   * @param workspaceId
   * @param parentId
   * @param addNote
   */
  return useCallback(
    ({ parentId, workspaceId }: { parentId: string; workspaceId: string }) => {
      let modalTitle = t('webnotes-create_subfolder');
      let folderDefaultTitle = t('webnotes-new_subfolder');
      if (parentId === 'root') {
        modalTitle = t('webnotes-create_folder');
        folderDefaultTitle = t('webnotes-new_folder');
      }

      openModal({
        title: modalTitle,
        content: PopupType.RENAME,
        showCloseButton: true,
        closeButtonSize: 18,
        payload: {
          type: 'folder',
          value: folderDefaultTitle,
          payloadType: PopupType.RENAME,
          themeColors: webnotesThemeColors,
          themeStyle: webnotesThemeStyle,
          onApply: async (value: string) => {
            const subfolder = await createWorkspaceFolder({
              workspaceId,
              folderName: value,
              parentId,
            });

            if (addNote) {
              addNote(subfolder);
            }

            router.push('/space/' + workspaceId + '/folder/' + subfolder.globalId);
          },
        },
      });
    },
    [addNote, openModal, router, t]
  );
};

export const useDuplicateFolder = () => {
  const [orgInfo, currentOrganization, orgRoleState, currentWorkspace] = useUnit([
    $orgInfo,
    $currentOrganization,
    $orgRoleState,
    $currentWorkspace,
  ]);
  const [startDuplicateFolderTask, openModal] = useEvent([startDuplicateFolderTaskEvent, openModalEvent]);
  const openPaywallPopup = useOpenPaywallPopup();
  const { toast } = useToast();

  return useCallback(
    ({ itemId, itemParentId, itemName }: { itemId: string; itemParentId: string; itemName: string }) => {
      if (orgInfo?.subscriptionType === OrgInfoSubscriptionType.STANDARD) {
        const paywallTitle =
          'Your work, simplified. Duplicate folders, scan your documents and achieve way more with our advanced plan!';
        const paywallPopupPayload: PaywallPopupPayload = {
          payloadType: PopupType.PAYWALL,
          title: paywallTitle,
          reason: PaywallPopupReason.DUPLICATE_FOLDER,
          pricingReason: PaywallPopupPricingReason.MENU_UPGRADE_TO_PRO,
          orgId: currentOrganization.globalId,
        };

        const noPermissionsPopupPayload: DialogPopupPayload = {
          payloadType: PopupType.DIALOG,
          text: 'Folder duplication does not apply to the Free plan. Contact the workspace owner for more details.',
          buttons: [
            {
              text: 'OK',
            },
          ],
        };

        const noPermissionsPopup = {
          title: 'No permissions to duplicate folder',
          content: PopupType.DIALOG,
          showCloseButton: false,
          closeButtonSize: 18,
          payload: noPermissionsPopupPayload,
        };

        if (orgRoleState?.role !== 'owner') {
          openModal(noPermissionsPopup);
        } else {
          openPaywallPopup(paywallPopupPayload);
        }
      } else {
        startDuplicateFolderTask({
          workspaceId: currentWorkspace.globalId,
          globalId: itemId,
          parentId: itemParentId,
          folderName: itemName,
          callback: (data) => {
            if (!data) {
              toast?.({ title: 'Duplicate error' });
              return;
            }
            if ((data?.cnt ?? 0) <= ITEMS_TO_SHOW_DUPLICATION_PROGRESS) {
              toast?.({ title: 'Duplication started' });
            }
          },
        });
      }
    },
    [
      currentOrganization.globalId,
      currentWorkspace.globalId,
      openModal,
      openPaywallPopup,
      orgInfo?.subscriptionType,
      orgRoleState?.role,
      startDuplicateFolderTask,
      toast,
    ]
  );
};
